// Mirrors `QUICKCHAT_BUILTIN_VARIABLE_NAMES` / `QUICKCHAT_METADATA_PREFIX` in backend `quickchat/consts.py`; keep in sync.

export type AvailableVariable = {
  name: string;
  description: string;
  exampleValue?: string;
};

export const BUILTIN_VARIABLES: readonly AvailableVariable[] = [
  {
    name: 'scenario_id',
    description: 'Identifier of this AI Agent. Visible in your browser URL.',
    // exampleValue is the live SCENARIO_ID, resolved in ParameterDropdown
  },
  { name: 'conversation_id', description: 'Unique identifier of this conversation' },
  { name: 'conversation_url', description: 'Direct link to this conversation in the Inbox' },
  {
    name: 'conversation_channel',
    description: 'Channel name (widget, telegram, slack, intercom, whatsapp, etc.)',
    exampleValue: 'widget',
  },
  {
    name: 'current_timestamp',
    description: 'Current UTC timestamp in ISO 8601 format',
    exampleValue: '2026-09-02T12:00:00+00:00',
  },
  {
    name: 'language',
    description: "Visitor's language as a 2-letter code",
    exampleValue: 'en',
  },
  {
    name: 'country',
    description: "Visitor's country as a 2-letter code",
    exampleValue: 'US',
  },
] as const;

// `metadata_locale` is intentionally excluded: it overlaps with `language` + `country` built-ins; if a scenario uses it, it surfaces via observed-keys.
export const VISITOR_METADATA_VARIABLES: readonly AvailableVariable[] = [
  { name: 'metadata_fullPathURL', description: 'Current page URL' },
  { name: 'metadata_visitor_name', description: "Visitor's name" },
  { name: 'metadata_visitor_email', description: "Visitor's email" },
  { name: 'metadata_visitor_phone_number', description: "Visitor's phone number" },
] as const;

const VISITOR_METADATA_NAME_SET = new Set(VISITOR_METADATA_VARIABLES.map((v) => v.name));
export const isVisitorMetadataVariableName = (name: string): boolean =>
  VISITOR_METADATA_NAME_SET.has(name);

export const METADATA_PREFIX = 'metadata_';

const BUILTIN_VARIABLE_NAME_SET = new Set(BUILTIN_VARIABLES.map((v) => v.name));

export const isBuiltinVariableName = (name: string): boolean => BUILTIN_VARIABLE_NAME_SET.has(name);

export const isMetadataVariableName = (name: string): boolean =>
  name.startsWith(METADATA_PREFIX) && name.length > METADATA_PREFIX.length;
